/**
 * TF App Switcher - Standalone Web Component
 *
 * A framework-agnostic app switcher that works anywhere.
 * No build step required - just include and use.
 *
 * Usage:
 *   <script src="tf-app-switcher.standalone.js"></script>
 *   <tf-app-switcher current-app="pulse" position="left" theme="light"></tf-app-switcher>
 */

(function () {
  "use strict";

  // ============================================
  // ENVIRONMENT DETECTION
  // ============================================
  function getEnvironment() {
    const hostname = window.location.hostname;
    if (hostname.includes(".dev.food-digital.tomra.com")) return "dev";
    if (hostname.includes(".qa.food-digital.tomra.com")) return "qa";
    if (hostname.includes(".food-digital.tomra.com")) return "prod";
    return "dev"; // localhost / unknown defaults to dev
  }

  // ============================================
  // APP CONFIGURATION
  // ============================================
  const DOCS_URLS = {
    dev: "https://docs.dev.food-digital.tomra.com/",
    qa: "https://docs.qa.food-digital.tomra.com/projects",
    prod: "https://docs.tomra.com",
  };

  const MLOPS_URLS = {
    dev: "https://mlops.dev.food-digital.tomra.com/applications",
    qa: "https://mlops.qa.food-digital.tomra.com/applications",
    prod: "https://mlops.food-digital.tomra.com/applications",
  };

  function getApps() {
    const env = getEnvironment();
    return [
      {
        id: "pulse",
        name: "TF Pulse",
        url: "/",
        description: "Equipment monitoring dashboard",
        icon: "P",
        iconColor: "#9c77ec",
      },
      {
        id: "mlops",
        name: "MLOps",
        url: MLOPS_URLS[env],
        description: "Machine learning operations",
        icon: "M",
        iconColor: "#bb6706",
      },
      {
        id: "docs",
        name: "Documentation",
        url: DOCS_URLS[env],
        description: "Technical documentation",
        icon: "D",
        iconColor: "#10b981",
      },
    ];
  }

  function getAppsWithActive(currentAppId) {
    return getApps().map((app) => ({
      ...app,
      active: app.id === currentAppId,
    }));
  }

  // ============================================
  // STYLES
  // ============================================
  const styles = `
    :host {
      display: inline-block;
      position: relative;
      font-family: 'Nunito Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      --tf-as-bg: #ffffff;
      --tf-as-bg-hover: #f3f4f6;
      --tf-as-text: #464c52;
      --tf-as-text-secondary: #464c52;
      --tf-as-border: #e7eaee;
      --tf-as-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
      --tf-as-active-bg: #eff6ff;
      --tf-as-active-border: #3b82f6;
    }

    :host([theme="dark"]) {
      --tf-as-bg: #1f2937;
      --tf-as-bg-hover: #374151;
      --tf-as-text: #f9fafb;
      --tf-as-text-secondary: #9ca3af;
      --tf-as-border: #374151;
      --tf-as-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
      --tf-as-active-bg: #1e3a5f;
    }

    * { box-sizing: border-box; }

    .trigger {
      display: flex;
      align-items: center;
      justify-content: center;
      width: var(--tf-as-trigger-size, 32px);
      height: var(--tf-as-trigger-size, 32px);
      border: 1px solid transparent;
      background: transparent;
      border-radius: 4px;
      cursor: pointer;
      transition: background-color 0.15s ease, border-color 0.15s ease;
      padding: 6px;
      color: #006bc9;
    }

    :host([size="sm"]) {
      --tf-as-trigger-size: 28px;
      --tf-as-icon-size: 14px;
      --tf-as-dot-width: 3px;
      --tf-as-dot-gap: 2px;
    }

    :host([size="lg"]) {
      --tf-as-trigger-size: 40px;
      --tf-as-icon-size: 22px;
      --tf-as-dot-width: 5px;
      --tf-as-dot-gap: 4px;
    }

    :host([theme="light"]) .trigger {
      color: #006bc9;
    }

    :host([theme="dark"]) .trigger {
      color: #60a5fa;
    }

    .trigger:hover {
      background: rgba(0, 107, 201, 0.08);
    }

    .trigger:focus-visible {
      outline: 2px solid #006bc9;
      outline-offset: 2px;
    }

    .trigger[aria-expanded="true"] {
      border-color: #006bc9;
    }

    .trigger-icon {
      display: flex;
      gap: var(--tf-as-dot-gap, 3px);
      width: var(--tf-as-icon-size, 18px);
      height: var(--tf-as-icon-size, 18px);
      align-items: center;
      justify-content: center;
    }

    .trigger-bar {
      display: flex;
      flex-direction: column;
      gap: var(--tf-as-dot-gap, 3px);
      height: 100%;
    }

    .trigger-dot {
      width: var(--tf-as-dot-width, 4px);
      height: var(--tf-as-dot-width, 4px);
      background: currentColor;
      border-radius: 50%;
    }

    .dropdown {
      position: fixed;
      z-index: 9999;
      min-width: 200px;
      max-width: 360px;
      background: var(--tf-as-bg);
      border: 1px solid var(--tf-as-border);
      border-radius: 4px;
      box-shadow: var(--tf-as-shadow);
      opacity: 0;
      visibility: hidden;
      transform: translateY(-8px);
      transition: opacity 0.2s ease, transform 0.2s ease, visibility 0.2s;
      pointer-events: none;
      overflow: hidden;
    }

    .dropdown.open {
      opacity: 1;
      visibility: visible;
      transform: translateY(0);
      pointer-events: auto;
    }

    .dropdown-header {
      padding: 8px;
      border-bottom: 1px solid var(--tf-as-border);
    }

    .dropdown-title {
      margin: 0;
      font-size: 13px;
      font-weight: 700;
      line-height: 1.2;
      color: var(--tf-as-text-secondary);
    }

    .apps-list {
      display: flex;
      flex-direction: column;
    }

    .app-link {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 8px;
      text-decoration: none;
      color: var(--tf-as-text);
      transition: background-color 0.15s ease;
      border: none;
    }

    .app-link:hover {
      background: var(--tf-as-bg-hover);
    }

    .app-link:focus-visible {
      outline: none;
      box-shadow: 0 0 0 2px var(--tf-as-active-border);
    }

    .app-link.active {
      background: var(--tf-as-active-bg);
    }

    .app-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      border-radius: 4px;
      font-size: 13px;
      font-weight: 700;
      color: #ffffff;
      flex-shrink: 0;
    }

    .app-name {
      font-size: 15px;
      font-weight: 400;
      line-height: 1.3;
    }

    .backdrop {
      position: fixed;
      inset: 0;
      z-index: 9998;
      display: none;
    }

    .backdrop.visible {
      display: block;
    }
  `;

  // ============================================
  // WEB COMPONENT
  // ============================================
  class TfAppSwitcher extends HTMLElement {
    constructor() {
      super();
      this.shadow = this.attachShadow({ mode: "open" });
      this._apps = [];
      this._currentApp = "";
      this._isOpen = false;
      this.handleKeyDown = this.handleKeyDown.bind(this);
    }

    static get observedAttributes() {
      return ["current-app", "position", "theme", "size"];
    }

    connectedCallback() {
      if (!this.hasAttribute("position")) this.setAttribute("position", "left");
      if (!this.hasAttribute("theme")) this.setAttribute("theme", "auto");

      this._currentApp = this.getAttribute("current-app") || "";
      this._apps = getAppsWithActive(this._currentApp);

      this.render();
      this.setupEventListeners();
    }

    disconnectedCallback() {
      document.removeEventListener("keydown", this.handleKeyDown);
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (oldValue === newValue) return;
      if (name === "current-app") {
        this._currentApp = newValue || "";
        this._apps = getAppsWithActive(this._currentApp);
        this.render();
        this.setupEventListeners();
      }
    }

    get position() {
      return this.getAttribute("position") || "left";
    }

    set position(value) {
      this.setAttribute("position", value);
    }

    get theme() {
      return this.getAttribute("theme") || "auto";
    }

    set theme(value) {
      this.setAttribute("theme", value);
    }

    toggle() {
      this._isOpen = !this._isOpen;
      this.updateDropdownState();
    }

    close() {
      this._isOpen = false;
      this.updateDropdownState();
    }

    updateDropdownState() {
      const dropdown = this.shadow.querySelector(".dropdown");
      const backdrop = this.shadow.querySelector(".backdrop");
      const trigger = this.shadow.querySelector(".trigger");

      if (dropdown) {
        if (this._isOpen) {
          dropdown.classList.add("open");

          // Position dropdown relative to trigger
          if (trigger) {
            const rect = trigger.getBoundingClientRect();
            dropdown.style.top = `${rect.bottom + 8}px`;
            dropdown.style.left =
              this.position === "right" ? "auto" : `${rect.left}px`;
            dropdown.style.right =
              this.position === "right"
                ? `${window.innerWidth - rect.right}px`
                : "auto";
          }
        } else {
          dropdown.classList.remove("open");
        }
      }

      if (backdrop) {
        if (this._isOpen) {
          backdrop.classList.add("visible");
        } else {
          backdrop.classList.remove("visible");
        }
      }

      if (this._isOpen) {
        document.addEventListener("keydown", this.handleKeyDown);
      } else {
        document.removeEventListener("keydown", this.handleKeyDown);
      }
    }

    handleKeyDown(e) {
      if (e.key === "Escape") {
        this.close();
        this.shadow.querySelector(".trigger")?.focus();
      }
    }

    setupEventListeners() {
      const trigger = this.shadow.querySelector(".trigger");
      const backdrop = this.shadow.querySelector(".backdrop");

      if (trigger) {
        trigger.onclick = (e) => {
          e.stopPropagation();
          this.toggle();
        };
      }

      if (backdrop) {
        backdrop.onclick = (e) => {
          e.stopPropagation();
          this.close();
        };
      }

      // App links
      this.shadow.querySelectorAll(".app-link").forEach((link, i) => {
        link.onclick = (e) => {
          const app = this._apps[i];

          this.dispatchEvent(
            new CustomEvent("app-click", {
              detail: { app },
              bubbles: true,
              composed: true,
            }),
          );

          if (app.active) {
            e.preventDefault();
            this.close();
          }
        };
      });
    }

    render() {
      this.shadow.innerHTML = `
        <style>${styles}</style>
        <div class="backdrop"></div>
        <button class="trigger" aria-label="Open app switcher" aria-haspopup="true" aria-expanded="${this._isOpen}">
          <div class="trigger-icon">
            <div class="trigger-bar">
              <div class="trigger-dot"></div>
              <div class="trigger-dot"></div>
              <div class="trigger-dot"></div>
            </div>
            <div class="trigger-bar">
              <div class="trigger-dot"></div>
              <div class="trigger-dot"></div>
              <div class="trigger-dot"></div>
            </div>
            <div class="trigger-bar">
              <div class="trigger-dot"></div>
              <div class="trigger-dot"></div>
              <div class="trigger-dot"></div>
            </div>
          </div>
        </button>
        <div class="dropdown" role="menu">
          <div class="dropdown-header">
            <h3 class="dropdown-title">Switch to</h3>
          </div>
          <div class="apps-list">
            ${this._apps
              .map(
                (app) => `
              <a
                href="${app.url}"
                class="app-link ${app.active ? "active" : ""}"
                role="menuitem"
                title="${app.description || app.name}"
              >
                <span class="app-icon" style="background-color: ${app.iconColor || "#6b7280"}">${app.icon || app.name.charAt(0)}</span>
                <span class="app-name">${app.name}</span>
              </a>
            `,
              )
              .join("")}
          </div>
        </div>
      `;
    }
  }

  // Register the component
  if (!customElements.get("tf-app-switcher")) {
    customElements.define("tf-app-switcher", TfAppSwitcher);
  }
})();
